import React, { useEffect, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Chip,
    ListSubheader,
    TextField,
    Typography,
} from "@mui/material";
import LoadingDots from "./LoadingDots";
import { ds } from "../../styles/designSystem";

/** 매 렌더 새 배열을 만들면 MUI 가 후보가 바뀐 줄 알고 하이라이트를 흔든다 — 참조 고정. */
const EMPTY_OPTIONS = [];

/**
 * 공통 자동완성/드롭다운 — MUI Autocomplete 의 사내 표준 래퍼.
 *
 * <h3>왜 필요한가</h3>
 * 거래처·메일 수신자·직원/부서 필터 등 검색형 입력이 6~7곳에 흩어져 있고, 각자
 * 디바운스·로딩 표기·칩 스타일·드롭다운 위치를 다르게 구현해 왔다. 본 컴포넌트는
 * 그 패턴을 단일 API 로 통합하기 위한 표준이다.
 *
 * <h3>핵심 차별점</h3>
 * <ul>
 *   <li><b>드롭다운은 항상 input 아래</b> — Popper flip 모디파이어를 끔으로써
 *       위로 뒤집히지 않게 강제한다 ({@code disableFlip=true}, 기본 on).</li>
 *   <li><b>"Loading..." 텍스트 없음</b> — MUI 기본 문구 대신 {@link LoadingDots} 의
 *       스피너 + 점 애니메이션만 보여준다.</li>
 *   <li>비동기 fetcher 모드 — {@code fetcher(q)} 만 넘기면 디바운스/로딩/dedupe 까지
 *       자동 처리.</li>
 * </ul>
 *
 * <h3>props</h3>
 * <ul>
 *   <li>{@code multiple, freeSolo} — MUI 와 동일</li>
 *   <li>{@code value, onChange(newValue, reason, detail)}</li>
 *   <li>{@code options} 또는 {@code fetcher(q) => Promise<array>} — 둘 중 하나</li>
 *   <li>{@code debounceMs=200}, {@code fetchOnOpen=true}, {@code fetcherDeps=[]}</li>
 *   <li>{@code getOptionLabel, isOptionEqualToValue, groupBy} — MUI 와 동일</li>
 *   <li>{@code secondaryText(option)} — 기본 renderOption 의 둘째 줄 텍스트</li>
 *   <li>{@code renderOption, renderTag, renderGroup} — 필요 시 커스텀</li>
 *   <li>{@code validate(text) → string|null} — freeSolo 커밋 시 에러 메시지 반환</li>
 *   <li>{@code onCommitInvalid(text, error)} — 검증 실패 시 콜백 (notify 등)</li>
 *   <li>{@code variant="outlined"|"standard"}, {@code disableUnderline}</li>
 *   <li>{@code disableFlip=true} — false 로 두면 viewport 에 맞춰 위로 뒤집힐 수 있음</li>
 * </ul>
 *
 * <h3>비동기 모드의 "낡은 후보" 차단 (2026-08)</h3>
 * fetcher 모드는 {@code filterOptions=(x)=>x} 로 MUI 자체 필터링을 끄기 때문에 드롭다운 후보가
 * 곧 {@code asyncOptions} 다. 그런데 이 목록은 디바운스로만 갱신되므로 <b>타이핑 직후 debounceMs
 * 동안 «직전 질의어의 결과»가 그대로 떠 있다</b>. 여기에 {@code autoHighlight} 가 겹치면 0번 후보가
 * 계속 하이라이트 상태이고, MUI 는 Enter 를 «하이라이트 확정»으로 먼저 처리하므로 freeSolo 커밋
 * 분기까지 가지도 못한다. 더 나쁜 건 그 후보가 <b>이미 선택된 값</b>일 때다 — MUI 의 selectNewValue 는
 * 이미 있는 값을 다시 고르면 «토글 해제»로 보고 splice 해버려서, 엉뚱한 텍스트를 치고 Enter 를
 * 누른 사용자의 <b>기존 칩이 사라진다</b>(메일 받는이에서 실제로 발생).
 * 두 겹으로 막는다:
 * <ol>
 *   <li>{@code optionsQuery} 로 «지금 입력을 반영한 목록인지» 추적 → 낡았으면 후보를 비워
 *       하이라이트 자체를 없애고, 그 사이 들어온 Enter 는 삼킨다(결과 도착 후 다시 Enter).</li>
 *   <li>안전망 — Enter·blur 로 발생한 {@code removeOption} 은 무조건 무시한다({@code autoSelect} 는
 *       blur 로도 같은 토글 해제를 일으킨다). 사용자가 칩을 지우는 경로는 delete 아이콘 클릭과
 *       Backspace 뿐이고, 그 둘은 그대로 통과한다.</li>
 * </ol>
 */
export default function AppAutocomplete({
    // mode
    multiple = false,
    freeSolo = false,

    // value
    value,
    onChange,
    inputValue: inputValueProp,
    onInputChange,

    // data source
    options: staticOptions = [],
    fetcher,
    fetcherDeps = [],
    debounceMs = 500,
    fetchOnOpen = true,

    // identity
    getOptionLabel = (o) => (typeof o === "string" ? o : (o?.name || "")),
    getOptionKey,
    isOptionEqualToValue,
    groupBy,

    // render
    renderOption,
    renderTag,
    renderGroup,
    secondaryText,

    // input
    label,
    placeholder,
    helperText,
    autoFocus = false,
    size = "small",
    variant = "outlined",
    disableUnderline = false,
    startAdornment,
    fullWidth = true,
    /** 내부 TextField 의 sx 오버라이드 — 외부 페이지(키비주얼 등) 의 inputSoft 같은
     *  도메인 톤을 적용할 때 사용. AppAutocomplete 의 sx 는 wrapper(Autocomplete) 에 적용되어
     *  TextField 톤(테두리·배경·radius)을 바꾸지 못하므로 별도 prop 으로 분리. */
    textFieldSx,

    // ux
    noOptionsText = "검색 결과가 없습니다",
    emptyText = "검색어를 입력하세요",
    validate,
    onCommitInvalid,
    onKeyDown,

    // popper
    dropdownMaxHeight = 320,
    disableFlip = true,

    // misc
    sx,
    disabled = false,
    ...rest
}) {
    const [innerInput, setInnerInput] = useState("");
    const inputValue = inputValueProp ?? innerInput;
    const updateInput = (v) => {
        if (onInputChange) onInputChange(v);
        else setInnerInput(v);
    };

    const [asyncOptions, setAsyncOptions] = useState([]);
    /** asyncOptions 를 만들어 낸 질의어. null = 아직 한 번도 안 받음 → 현재 입력과 다르면 «낡음» */
    const [optionsQuery, setOptionsQuery] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);
    /** 응답 순서 보장 — 느린 이전 요청이 뒤늦게 도착해 최신 결과를 덮어쓰면 optionsQuery 가 거짓말이 된다. */
    const reqSeqRef = useRef(0);
    /** 후보를 기다리느라 보류한 Enter — {@code {query, el}}. 결과 도착 직후 재생한다. */
    const pendingEnterRef = useRef(null);

    const isAsync = typeof fetcher === "function";
    const query = (inputValue || "").trim();
    /** 후보 목록이 현재 입력을 아직 반영하지 못한 상태(디바운스 대기·요청 진행 중) */
    const optionsStale = isAsync && optionsQuery !== query;
    const options = isAsync ? (optionsStale ? EMPTY_OPTIONS : asyncOptions) : staticOptions;

    /* 디바운스 fetch — open 일 때만, 빈 입력은 fetchOnOpen 에 따름 */
    useEffect(() => {
        if (!isAsync || !open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const q = (inputValue || "").trim();
        if (!q && !fetchOnOpen) {
            setAsyncOptions(EMPTY_OPTIONS);
            setOptionsQuery(q);
            return;
        }
        debounceRef.current = setTimeout(async () => {
            const seq = ++reqSeqRef.current;
            setLoading(true);
            try {
                const res = await fetcher(q);
                if (seq !== reqSeqRef.current) return;      // 뒤늦게 온 이전 응답 — 폐기
                setAsyncOptions(Array.isArray(res) ? res : EMPTY_OPTIONS);
                setOptionsQuery(q);
            } catch {
                if (seq !== reqSeqRef.current) return;
                setAsyncOptions(EMPTY_OPTIONS);
                setOptionsQuery(q);
            } finally {
                if (seq === reqSeqRef.current) setLoading(false);
            }
        }, debounceMs);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, open, isAsync, ...fetcherDeps]);

    /* MUI 의 «토글 해제» 차단 — Enter·blur 는 칩 삭제 수단이 아니다.
       selectNewValue 는 이미 선택된 후보를 다시 고르면 reason 을 removeOption 으로 바꿔 splice 한다.
       autoHighlight(+autoSelect) 와 만나면 사용자가 의도한 적 없는 Enter 한 번, 혹은 그냥 딴 데를
       클릭한 것만으로 기존 칩이 사라진다.
       의도적인 삭제 경로 — delete 아이콘 click, Backspace keydown — 는 여기 걸리지 않고 그대로 통과. */
    const isAccidentalRemove = (event, reason) =>
        reason === "removeOption" &&
        (event?.type === "blur" ||
         event?.type === "focusout" ||
         (event?.type === "keydown" && event?.key === "Enter"));

    /* 후보가 낡은 동안의 Enter 는 «보류» 한다 — 삼키기만 하면 사용자 입장에선 Enter 가 먹통이다.
       삼키지 않으면 «이름 치고 바로 Enter» 가 후보 없는 freeSolo 로 흘러, 실은 존재하는 사람인데도
       "형식이 아닙니다" 를 띄운다. 그래서 이 Enter 를 기억해 두었다가 결과가 도착한 직후 재생한다 —
       사용자는 한 번만 누르고, 결과가 뜨는 시점에 «후보 확정» 또는 «검증 실패 안내» 중 맞는 쪽이 난다.
       단, 지금 친 텍스트만으로 이미 확정 가능한 경우(외부 메일주소 직접 입력 등)는 기다릴 이유가
       없으므로 그대로 통과시킨다 — 후보 목록과 무관하게 freeSolo 가 올바르게 커밋한다.
       defaultMuiPrevented 는 useAutocomplete 가 «이 키는 내가 처리하지 말라» 로 읽는 공식 신호. */
    const handleRootKeyDown = (event) => {
        onKeyDown?.(event);
        if (event.defaultMuiPrevented) return;
        if (event.key !== "Enter" || !isAsync || !open || !query) return;
        if (!optionsStale && !loading) return;
        const commitableAsIs = freeSolo && (!validate || !validate(query));
        if (commitableAsIs) return;
        pendingEnterRef.current = { query, el: event.target };
        event.defaultMuiPrevented = true;
    };

    /* 보류된 Enter 재생 — 목록이 현재 입력을 반영한 순간 그때의 Enter 를 그대로 다시 흘려보낸다.
       직접 커밋하지 않고 키를 재생하는 이유: 후보 확정·중복 제거·입력 리셋 규칙을 useAutocomplete 가
       이미 갖고 있어, 여기서 흉내 내면 두 벌이 되고 갈라진다.
       그새 사용자가 더 쳤거나(입력 변경) 팝업을 닫았으면 취소한다 — 뒤늦게 혼자 확정되면 안 된다. */
    useEffect(() => {
        const pending = pendingEnterRef.current;
        if (!pending) return;
        if (!open || pending.query !== query) { pendingEnterRef.current = null; return; }
        if (optionsStale || loading) return;
        pendingEnterRef.current = null;
        pending.el?.dispatchEvent(
            new KeyboardEvent("keydown", { key: "Enter", code: "Enter", bubbles: true, cancelable: true })
        );
    }, [optionsStale, loading, query, open]);

    /* freeSolo 커밋 검증 — onChange reason 이 createOption / blur 일 때 마지막 입력값 검사 */
    const handleChange = (event, newVal, reason, detail) => {
        if (isAccidentalRemove(event, reason)) return;
        if (freeSolo && (reason === "createOption" || reason === "blur") && validate) {
            const last = multiple ? newVal[newVal.length - 1] : newVal;
            const t = typeof last === "string" ? last.trim() : null;
            if (t) {
                const err = validate(t);
                if (err) {
                    onCommitInvalid?.(t, err);
                    return;
                }
            }
        }
        onChange?.(newVal, reason, detail);
    };

    /* 기본 renderOption — main + sub 두 줄 */
    const defaultRenderOption = (props, option) => {
        const { key, ...restProps } = props;
        const k = getOptionKey
            ? getOptionKey(option)
            : (typeof option === "string" ? option : (option?.id ?? getOptionLabel(option)));
        const main = getOptionLabel(option);
        const sub = secondaryText?.(option);
        return (
            <Box
                component="li"
                key={k}
                {...restProps}
                sx={{
                    py: 0.6,
                    px: 1.2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 0,
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: ds.colors.textPrimary, lineHeight: 1.35 }}>
                    {main}
                </Typography>
                {sub && (
                    <Typography sx={{ fontSize: 11, color: ds.colors.secondary, lineHeight: 1.35 }}>
                        {sub}
                    </Typography>
                )}
            </Box>
        );
    };

    /* 기본 renderGroup — 회색 톤 ListSubheader */
    const defaultRenderGroup = (params) => (
        <li key={params.key}>
            {params.group && (
                <ListSubheader
                    sx={{
                        lineHeight: "22px",
                        fontSize: 11,
                        fontWeight: 700,
                        color: ds.colors.secondary,
                        bgcolor: "#f8fafc",
                    }}
                >
                    {params.group}
                </ListSubheader>
            )}
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
    );

    /* 기본 renderTag — primaryBg 칩 */
    const defaultRenderTag = (val, index, getTagProps) => {
        const lbl = typeof val === "string" ? val : getOptionLabel(val);
        return (
            <Chip
                key={index}
                label={lbl}
                size="small"
                {...getTagProps({ index })}
                sx={{
                    fontSize: 11,
                    height: 22,
                    fontWeight: 500,
                    bgcolor: ds.colors.primaryBg,
                    color: ds.colors.primaryText,
                    "& .MuiChip-deleteIcon": { color: ds.colors.primaryText },
                }}
            />
        );
    };

    const popperModifiers = disableFlip
        ? [{ name: "flip", enabled: false }]
        : undefined;

    return (
        <Autocomplete
            multiple={multiple}
            freeSolo={freeSolo}
            size={size}
            fullWidth={fullWidth}
            disabled={disabled}
            value={value}
            inputValue={inputValue}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onInputChange={(_, v) => updateInput(v)}
            onChange={handleChange}
            onKeyDown={handleRootKeyDown}
            options={options}
            loading={isAsync ? (loading || optionsStale) : false}
            filterOptions={isAsync ? (x) => x : undefined}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            groupBy={groupBy}
            noOptionsText={
                isAsync && !inputValue?.trim() && !fetchOnOpen ? emptyText : noOptionsText
            }
            loadingText={<LoadingDots compact label="" sx={{ py: 1.5 }} />}
            renderOption={renderOption || defaultRenderOption}
            renderGroup={groupBy ? (renderGroup || defaultRenderGroup) : undefined}
            renderTags={
                multiple
                    ? (values, getTagProps) =>
                        values.map((v, i) => (renderTag || defaultRenderTag)(v, i, getTagProps))
                    : undefined
            }
            slotProps={{
                /* 팝업(paper)·항목 hover/선택색·글자크기 등 드롭다운 "디자인"은
                 * styles/muiTheme.js 의 MuiAutocomplete styleOverrides 가 전담한다(공통 dropdownPaper/Item).
                 * 여기 slotProps 에는 이 컴포넌트만의 "동작/구조"(팝업 flip 끔, 2줄 옵션 좌측 정렬)만 남긴다. */
                popper: {
                    placement: "bottom-start",
                    ...(popperModifiers ? { modifiers: popperModifiers } : {}),
                },
                listbox: {
                    sx: {
                        maxHeight: dropdownMaxHeight,   // 이 컴포넌트는 호출부에서 높이를 조절할 수 있어 남긴다
                        /* MUI v7 의 listbox 슬롯이 옵션에 alignItems:center 를 descendant selector 로 박아둠.
                         * 우리 renderOption 들은 flexDirection:column(2줄) 을 쓰기 때문에 cross-axis 가 가로가 되어
                         * 텍스트가 가운데로 밀려버림. 동일 specificity 의 descendant selector 로 덮어쓴다. */
                        "& .MuiAutocomplete-option": {
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            textAlign: "left",
                        },
                    },
                },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    autoFocus={autoFocus}
                    label={label || undefined}
                    placeholder={placeholder}
                    helperText={helperText}
                    variant={variant}
                    sx={textFieldSx}
                    InputProps={{
                        ...params.InputProps,
                        ...(disableUnderline ? { disableUnderline: true } : {}),
                        sx: { fontSize: 13, gap: 0.5, ...(params.InputProps.sx || {}) },
                        startAdornment: startAdornment ? (
                            <>
                                {startAdornment}
                                {params.InputProps.startAdornment}
                            </>
                        ) : params.InputProps.startAdornment,
                    }}
                />
            )}
            sx={{ ...sx }}
            {...rest}
        />
    );
}
